import { Injectable } from '@angular/core';
import Pica from 'pica';

@Injectable({
  providedIn: 'root',
})
export class ImageProcessingService {
  private pica = Pica();

  constructor() { }

  async resizeImage(
    file: File,
    maxWidth: number,
    maxHeight: number,
    callback: (resizedImageSrc: string, blob: Blob) => void,
    shouldResize: boolean = true
  ): Promise<void> {
    try {
      // if (environment.name === 'dev') {
      //   console.info(`Original File Size: ${this.formatBytes(file.size)}`);
      // }

      const img = await this.loadImage(await this.readAsDataURL(file));

      if (!shouldResize) {
        const blob = await this.canvasToBlob(
          this.createCanvasFromImage(img),
          file.type,
          1.0
        );
        if (blob) {
          const base64 = await this.blobToBase64(blob);
          callback(base64, blob);
        }
        return;
      }

      const { width: newWidth, height: newHeight } = this.calculateDimensions(
        img.width,
        img.height,
        maxWidth,
        maxHeight
      );

      const targetCanvas = document.createElement('canvas');
      targetCanvas.width = newWidth;
      targetCanvas.height = newHeight;

      await this.pica.resize(img, targetCanvas, {
        unsharpAmount: 80,
        unsharpRadius: 0.6,
        unsharpThreshold: 2,
      });

      const blob = await this.canvasToBlob(targetCanvas, 'image/png', 0.9);

      if (blob) {
        const base64 = await this.blobToBase64(blob);
        callback(base64, blob);
      } else {
        console.error('Failed to create a Blob from canvas');
      }
    } catch (error) {
      console.error('Error processing image:', error);
    }
  }

  private readAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  private canvasToBlob(
    canvas: HTMLCanvasElement,
    type: string,
    quality: number
  ): Promise<Blob | null> {
    return new Promise((resolve) => {
      canvas.toBlob(resolve, type, quality);
    });
  }

  private resizeCanvas(
    image: HTMLImageElement,
    maxWidth: number,
    maxHeight: number
  ): HTMLCanvasElement {
    const ratio = Math.min(maxWidth / image.width, maxHeight / image.height);
    const newWidth = image.width * ratio;
    const newHeight = image.height * ratio;
    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(image, 0, 0, newWidth, newHeight);
    } else {
      throw new Error('Failed to get drawing context from canvas');
    }
    return canvas;
  }

  private calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number; } {
    const ratio = Math.min(
      maxWidth / originalWidth,
      maxHeight / originalHeight
    );

    if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
      return {
        width: originalWidth,
        height: originalHeight
      };
    }

    return {
      width: Math.round(originalWidth * ratio),
      height: Math.round(originalHeight * ratio)
    };
  }

  private createCanvasFromImage(img: HTMLImageElement): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0);
    }
    return canvas;
  }

  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }
}
